import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { BookOpen, Download, AlertCircle, RefreshCw, History } from 'lucide-react';

/**
 * Complete Architecture — full markdown viewer for TDC_COMPLETE_ARCHITECTURE.md.
 * Every agent MUST read this before touching code.
 * Source file: /app/memory/TDC_COMPLETE_ARCHITECTURE.md
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompleteArchitecturePage() {
  const [md, setMd] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadDoc = () => {
    setLoading(true);
    api.get('/docs/complete-architecture')
      .then((r) => {
        setMd(r.data.markdown || '');
        setInfo(r.data.file_info || null);
        setLoading(false);
      })
      .catch((e) => {
        setErr(e.response?.data?.detail || e.message);
        setLoading(false);
      });
  };

  useEffect(() => { loadDoc(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const r = await api.post('/operator-mira/refresh-architecture');
      const counts = r.data.counts || {};
      setRefreshMsg(
        `Refreshed · ${r.data.diff_summary} · ${counts.collections}c / ` +
        `${counts.api_routes}r / ${counts.frontend_routes}p / ${counts.crons}j / ` +
        `${counts.mira_intents}i`
      );
      loadDoc();
    } catch (e) {
      setRefreshMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleHistory = async () => {
    if (!showHistory && history.length === 0) {
      try {
        const r = await api.get('/operator-mira/architecture-history?limit=15');
        setHistory(r.data.items || []);
      } catch {
        setHistory([]);
      }
    }
    setShowHistory(!showHistory);
  };

  const handleDownload = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${API_URL}/api/docs/complete-architecture/download?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="arch-loading">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4" data-testid="arch-error">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle size={18} />
          <span className="font-semibold">Failed to load: {err}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-20" data-testid="complete-architecture-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
            <BookOpen size={20} color="white" />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Complete Architecture Document</h1>
            {info?.last_modified && (
              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                Last updated: {new Date(info.last_modified).toLocaleString('en-IN')} · {Math.round((info.size_bytes || 0) / 1024)} KB
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white hover:opacity-90 transition-all self-start"
          style={{ backgroundColor: '#2A7B88' }}
          data-testid="arch-download-btn">
          <Download size={13} /> Download .md
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border hover:bg-[#2A7B88] hover:text-white transition-all disabled:opacity-50"
          style={{ borderColor: '#2A7B88', color: refreshing ? '#9CA3AF' : '#2A7B88' }}
          data-testid="refresh-architecture-btn">
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh now (Mira auto-audit)'}
        </button>
        <button
          onClick={toggleHistory}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-gray-50 transition-all"
          style={{ borderColor: '#E5E1DC', color: '#4B5563' }}
          data-testid="arch-history-btn">
          <History size={13} /> {showHistory ? 'Hide history' : 'View refresh history'}
        </button>
      </div>

      {refreshMsg && (
        <div className="rounded-md px-3 py-2 mb-4 text-xs font-medium"
             style={{ backgroundColor: refreshMsg.startsWith('Failed') ? '#FEF2F2' : '#ECFDF5',
                      color: refreshMsg.startsWith('Failed') ? '#DC2626' : '#065F46' }}
             data-testid="arch-refresh-msg">
          {refreshMsg}
        </div>
      )}

      {showHistory && (
        <div className="rounded-lg border bg-white p-3 mb-4" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>
            Last {history.length} auto-refresh snapshots
          </p>
          <ul className="space-y-1 text-[11px]" style={{ color: '#4B5563' }}>
            {history.map((h, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="font-mono shrink-0">
                  {new Date(h.generated_at).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})}
                </span>
                <span>→</span>
                <span className="flex-1">{h.diff_summary} · {h.collections_summary?.count}c / {h.api_routes_summary?.total}r</span>
                <span className="text-[10px] italic" style={{ color: '#9CA3AF' }}>{h.triggered_by}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-xl border-2 p-4 mb-6" style={{ borderColor: '#2A7B88', backgroundColor: '#F0F8F9' }}>
        <p className="text-sm font-semibold mb-1" style={{ color: '#1B2A4A' }}>Agent Reading Rule</p>
        <p className="text-sm" style={{ color: '#4B5563' }}>
          Every agent inheriting this codebase MUST read this file (top to bottom) BEFORE touching any code.
          Update this file AFTER every build session that adds a collection, route, page, integration, cron, intent, or env var.
        </p>
      </div>

      <pre
        className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed p-5 rounded-lg border bg-white"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
        data-testid="arch-markdown">
        {md}
      </pre>
    </div>
  );
}
